import { useEffect, useState } from "react";
import { theme, btnPrimary } from "../theme";
import { api, ApiError } from "../api/client";
import { StatusTag, type StatusTone } from "./Drawing";
import { EvidenceRequestAnswerForm, EvidenceRequestAnswerSummary, type AnswerDocument, type SuggestedEvidenceDocument } from "./EvidenceRequestAnswer";

// Auditor feedback for one control, shown on the client's own control cards
// (above the notes thread) so the client does not have to visit the Auditor
// Feedback page to see what the auditor said. Findings and evidence requests
// are ticked off here; the same tick lives on the Actions page. The data comes
// with the notes thread (GET /controls/:id/comments) so a card costs one request.

export interface FindingComment {
  id: string;
  text: string;
  authorRole: string;
  createdAt: string;
  author: { displayName: string | null; email: string };
}

export interface InlineFinding {
  id: string;
  bureau: { name: string };
  text: string;
  status: string; // the auditor's verdict: open (needs work) | acknowledged (observation) | resolved (approved)
  clientStatus: string | null; // the client's own progress: acknowledged | resolved (awaiting verification)
  clientStatusAt: string | null;
  createdAt: string;
  comments: FindingComment[];
}

export interface InlineEvidenceRequest {
  id: string;
  message: string;
  status: string; // pending | acknowledged | fulfilled | withdrawn
  createdAt: string;
  bureau: { name: string };
  /** The newest version of each document already filed under the control (one-click answer). */
  suggestedDocuments?: SuggestedEvidenceDocument[];
  /** What answered it, once fulfilled (backend lib/evidenceRequests.ts). */
  responseNote?: string | null;
  fulfilledAt?: string | null;
  documents?: AnswerDocument[];
}

type Finding = InlineFinding;
type EvidenceRequest = InlineEvidenceRequest;

// The auditor's verdict, in the auditor's own words.
const VERDICT: Record<string, { tone: StatusTone; label: string }> = {
  open:         { tone: "gap",     label: "Needs work" },
  acknowledged: { tone: "partial", label: "Observation" },
  resolved:     { tone: "met",     label: "Approved" },
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const auditorBadge: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: "#f59e0b", background: "#f59e0b22", padding: "1px 7px", borderRadius: 3,
};

function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.status === 403) return "Your role does not permit this.";
  return e instanceof ApiError ? e.message : fallback;
}

function FindingCard({ finding, scope, onChange }: { finding: Finding; scope: Record<string, string | undefined>; onChange: (f: Finding) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const verdict = VERDICT[finding.status] ?? VERDICT.open;
  const approved = finding.status === "resolved";
  // The client's tick only ever moves clientStatus; the auditor's verdict is untouched.
  const clientResolved = finding.clientStatus === "resolved";
  // The auditor's "Approve control" records a resolved finding with no text.
  const isApproval = approved && !finding.text.trim();

  async function toggleResolved(checked: boolean) {
    setBusy(true);
    setError("");
    try {
      const status = checked ? "resolved" : "acknowledged";
      const updated = await api.patch<{ clientStatus: string | null; clientStatusAt: string | null }>(`/findings/${finding.id}/status`, { status }, scope);
      onChange({ ...finding, clientStatus: updated.clientStatus, clientStatusAt: updated.clientStatusAt });
    } catch (e) {
      setError(errorMessage(e, "Could not update the finding. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  if (isApproval) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12, color: theme.color.successText, background: "var(--c-successBg)", border: "1px solid var(--c-success)", borderRadius: 3, padding: "6px 12px" }}>
        <span style={auditorBadge}>Auditor</span>
        <span style={{ fontWeight: 600 }}>{finding.bureau.name} approved this control</span>
        <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>{fmtDate(finding.createdAt)}</span>
      </div>
    );
  }

  async function reply() {
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    setError("");
    try {
      const c = await api.post<FindingComment>(`/findings/${finding.id}/comments`, { text }, scope);
      onChange({ ...finding, comments: [...finding.comments, c] });
      setDraft("");
    } catch (e) {
      setError(errorMessage(e, "Could not send the reply. Please try again."));
    } finally {
      setPosting(false);
    }
  }

  const settled = approved || clientResolved;

  return (
    <div style={{ background: theme.color.panelAlt, border: `1px solid ${settled ? theme.color.border : "#f59e0b55"}`, borderRadius: 3, padding: "8px 12px", opacity: approved ? 0.8 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
        <span style={auditorBadge}>Auditor</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>{finding.bureau.name}</span>
        <StatusTag tone={verdict.tone}>{verdict.label}</StatusTag>
        {!approved && clientResolved && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 3, background: "var(--c-accentBg)", color: "var(--c-accentText)" }}>
            Resolved by you · awaiting auditor verification
          </span>
        )}
        {!approved && !clientResolved && finding.clientStatus === "acknowledged" && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 3, background: "var(--c-accentBg)", color: "var(--c-accentText)" }}>Acknowledged</span>
        )}
        <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>{fmtDate(finding.createdAt)}</span>
        {!approved && (
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: clientResolved ? theme.color.successText : theme.color.muted, cursor: busy ? "wait" : "pointer", userSelect: "none" }}>
            <input
              type="checkbox"
              checked={clientResolved}
              disabled={busy}
              onChange={(e) => toggleResolved(e.target.checked)}
              title={clientResolved ? "Untick to withdraw — your auditor has not verified this yet" : "Tick when you have addressed this finding; your auditor will verify it"}
              style={{ accentColor: theme.color.success, cursor: "pointer" }}
            />
            Resolved
          </label>
        )}
      </div>
      <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.6, whiteSpace: "pre-wrap", textDecoration: settled ? "line-through" : "none" }}>
        {finding.text || "No details recorded."}
      </div>

      {finding.comments.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8, paddingLeft: 10, borderLeft: `2px solid ${theme.color.border}` }}>
          {finding.comments.map((c) => (
            <div key={c.id}>
              <div style={{ display: "flex", gap: 6, alignItems: "baseline", marginBottom: 1 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: c.authorRole === "auditor" ? "#f59e0b" : theme.color.accentText }}>
                  {c.authorRole === "auditor" ? "Auditor" : (c.author.displayName ?? c.author.email)}
                </span>
                <span style={{ fontSize: 10, color: theme.color.faint }}>{fmtDate(c.createdAt)}</span>
              </div>
              <div style={{ fontSize: 12, color: theme.color.text, whiteSpace: "pre-wrap" }}>{c.text}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <input
          type="text"
          placeholder="Reply to the auditor…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); reply(); } }}
          style={{ flex: 1, background: "var(--c-inputBg)", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "5px 10px", fontSize: 12, color: theme.color.text, fontFamily: theme.font, outline: "none" }}
        />
        <button
          onClick={reply}
          disabled={posting || !draft.trim()}
          style={{ ...btnPrimary, fontSize: 11, padding: "5px 12px", opacity: posting || !draft.trim() ? 0.5 : 1 }}
        >
          {posting ? "Sending…" : "Send"}
        </button>
      </div>
      {error && <div style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function EvidenceRequestCard({ request, scope, onChange }: { request: EvidenceRequest; scope: Record<string, string | undefined>; onChange: (r: EvidenceRequest) => void }) {
  const [answering, setAnswering] = useState(false);
  const fulfilled = request.status === "fulfilled";

  return (
    <div style={{ background: theme.color.panelAlt, border: `1px solid ${fulfilled ? theme.color.border : "var(--c-warning)"}`, borderRadius: 3, padding: "8px 12px", opacity: fulfilled ? 0.85 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
        <span style={auditorBadge}>Auditor</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>Evidence requested by {request.bureau.name}</span>
        <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>{fmtDate(request.createdAt)}</span>
        {fulfilled
          ? <StatusTag tone="met">Answered</StatusTag>
          : !answering && (
            <button onClick={() => setAnswering(true)} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.text, cursor: "pointer" }}>
              Answer
            </button>
          )}
      </div>
      <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{request.message}</div>
      {fulfilled && <EvidenceRequestAnswerSummary documents={request.documents} note={request.responseNote} fulfilledAt={request.fulfilledAt} />}
      {answering && !fulfilled && (
        <EvidenceRequestAnswerForm<EvidenceRequest>
          requestId={request.id}
          scope={scope}
          suggestions={request.suggestedDocuments ?? []}
          onCancel={() => setAnswering(false)}
          onAnswered={(updated) => { setAnswering(false); onChange({ ...request, ...updated, bureau: request.bureau }); }}
        />
      )}
    </div>
  );
}

export function AuditorFeedbackInline({ findings: initialFindings, evidenceRequests: initialRequests, orgId }: {
  findings: InlineFinding[];
  evidenceRequests: InlineEvidenceRequest[];
  /** Only set in the consultant's management view, where the client org is not the caller's own. */
  orgId?: string;
}) {
  const [findings, setFindings] = useState<Finding[]>(initialFindings);
  const [requests, setRequests] = useState<EvidenceRequest[]>(initialRequests);
  const scope = { organizationId: orgId };
  useEffect(() => { setFindings(initialFindings); }, [initialFindings]);
  useEffect(() => { setRequests(initialRequests); }, [initialRequests]);

  if (findings.length === 0 && requests.length === 0) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Auditor feedback</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {findings.map((f) => (
          <FindingCard key={f.id} finding={f} scope={scope} onChange={(next) => setFindings((prev) => prev?.map((x) => x.id === next.id ? next : x) ?? prev)} />
        ))}
        {requests.map((r) => (
          <EvidenceRequestCard key={r.id} request={r} scope={scope} onChange={(next) => setRequests((prev) => prev.map((x) => x.id === next.id ? next : x))} />
        ))}
      </div>
    </div>
  );
}
